/**
 * @module High Speed Internet Services
 * @description
 */
import Head from "next/head";
import Tooltip from "@/blocks/Tooltip";

// Template
import Template from "@/templates/Default";

// Blocks
import HeroPricePoint from "@/blocks/Heroes/HeroPricePoint";
import BannerCTA from "@/blocks/Banners/BannerCTA";
import ColumnContent from "@/blocks/ColumnContent";
import BannerServiceCheckSingleLine from "@/blocks/Banners/BannerServiceCheckSingleLine";
import SplitSectionPriceCopper from "@/blocks/SplitSectionPrice/SplitSectionPriceCopper";
import FAQs from "@/blocks/FAQS/HighSpeedInternetFAQS";

// Cohesion
import MonarchFallback from "@/blocks/MonarchFallback";
import { MonarchSlot } from "@redventures/cohesion-utils-react";

/**
 * @function Page
 * @description
 */
const Page = () => (
  <>
    <Head>
      <title>High Speed Internet Services | Frontier</title>
      <meta
        name="description"
        content="High speed internet services from Frontier comes with everyday low prices. It's easy to switch so start now!"
      />
      <link
        rel="canonical"
        href="https://frontier.com/shop/internet/high-speed-internet-services"
      />
    </Head>

    <Template
      title="Get High Speed Internet Services at a Reliable Price"
      hero={
        <MonarchSlot
          placement="frdcHighSpeedInternetServicesHero"
          fallback={<MonarchFallback height={["750px", "498px", "643px"]} />}
        >
          <HeroPricePoint
            headline={
              <>
                Internet
                <br />
                <span key="span-1" className="u-color-blue">
                  without
                  <br />
                  limits
                </span>
              </>
            }
            subhead="Get internet with no data caps or overage charges"
            hash="copper"
            productName={
              <>
                Frontier<sup>&reg;</sup> Internet
              </>
            }
            background="kids-watching-movies-with-high-speed-internet"
            useSelectAreasLegal
          />
        </MonarchSlot>
      }
      primary={
        <>
          <BannerCTA copy="Call now to speak with an expert!" usePhone />

          <ColumnContent
            headline="Why Frontier Internet?"
            subhead="We make it easy to stay connected"
            theme="dark"
            useTiles
            items={[
              {
                icon: "speed-guage",
                headline: "No data caps or overage charges",
                content: (
                  <>
                    Work, learn, and live with unlimited data.
                    <Tooltip disclosure="data-caps" />
                  </>
                ),
              },
              {
                icon: "price-lock",
                headline: "Schedule installation",
                content: (
                  <>
                    We make setup easy with free installation available for
                    every fiber order.
                    <Tooltip disclosure="includes-tech-installation" />
                  </>
                ),
              },
              {
                icon: "wifi",
                headline: (
                  <>
                    Reliable eero router on us
                    <span data-legal="eero" />
                  </>
                ),
                content: "An intelligent eero router is included in your plan.",
              },
            ]}
          />

          <BannerServiceCheckSingleLine theme="blue" />

          <SplitSectionPriceCopper
            title="Work, learn, and live connected"
            items={[
              "No data caps or overage charges",
              <>
                Free installation
                <Tooltip disclosure="includes-tech-installation" />
              </>,
              <>
                Amazon eero 6 router included
                <span data-legal="eero" />
              </>,
            ]}
          />

          <FAQs />
        </>
      }
    ></Template>
  </>
);

export default Page;
